/**
 * Iteration A (QW2+QW3): pure derivations that turn a registry experiment +
 * (optional) A/B decision report row into a DECISION SURFACE — derived status,
 * trust line, typed gateSummary evidence, proposal feed, report-scope link.
 *
 * Display-only by contract (AC-802/809): nothing here mutates the registry or
 * feeds back into the state machine; the registry `state` chip stays untouched
 * next to the derived status chip.
 */

import type { ExperimentDocument } from './experimentTypes';
import type { FunnelABTestDecisionRow } from './types';
import { resolveDecisionVerdict, type DecisionVerdictResult } from './decisionVerdict';

/** Derived, display-only surface status (AC-802). Blocked wins. */
export type ExperimentSurfaceStatus =
  | 'delivery_only'
  | 'collecting'
  | 'ready_to_decide'
  | 'decided'
  | 'blocked_by_data_quality';

export const SURFACE_STATUS_LABEL: Record<ExperimentSurfaceStatus, string> = {
  delivery_only: 'Single-variant delivery',
  collecting: 'Collecting evidence',
  ready_to_decide: 'Ready to decide',
  decided: 'Decided',
  blocked_by_data_quality: 'Blocked by data quality',
};

/**
 * Pick the decision row for an experiment (AC-801). Several iterations can
 * fall into the fixed 30-day window; the currently running iteration dominates
 * it by the latest allocation activation time. Legacy rows without epoch
 * timestamps retain the assigned-users fallback.
 */
export function selectDecisionRow(
  experiment: Pick<ExperimentDocument, 'abTestRef'>,
  decisions: FunnelABTestDecisionRow[] | null | undefined,
): FunnelABTestDecisionRow | null {
  const abTestId = experiment.abTestRef?.abTestId;
  if (!abTestId || !decisions?.length) return null;
  const iterationId = experiment.abTestRef?.iterationId;
  const rows = decisions.filter(row => (
    row.testId === abTestId && (!iterationId || row.iterationId === iterationId)
  ));
  if (!rows.length) return null;
  const activatedAt = (row: FunnelABTestDecisionRow) => {
    const value = row.srm?.evaluatedFrom;
    return value ? Date.parse(value) : Number.NaN;
  };
  const assigned = (row: FunnelABTestDecisionRow) =>
    row.scores.reduce((sum, score) => sum + (score.assignedUsers || 0), 0);
  return rows.reduce((best, current) => {
    const bestActivated = activatedAt(best);
    const currentActivated = activatedAt(current);
    if (Number.isFinite(bestActivated) || Number.isFinite(currentActivated)) {
      if (!Number.isFinite(bestActivated)) return current;
      if (!Number.isFinite(currentActivated)) return best;
      if (currentActivated !== bestActivated) return currentActivated > bestActivated ? current : best;
    }
    const delta = assigned(current) - assigned(best);
    if (delta !== 0) return delta > 0 ? current : best;
    return current.iterationId.localeCompare(best.iterationId) > 0 ? current : best;
  });
}

/** Current single-variant delivery is not an active experiment card. Decided
 * and stopped registry rows remain visible as historical evidence. */
export function isActiveDeliveryOnlyExperiment(
  experiment: Pick<ExperimentDocument, 'state' | 'abTestRef'>,
  decisions: FunnelABTestDecisionRow[] | null | undefined,
): boolean {
  if (experiment.state !== 'running' && experiment.state !== 'ready_to_decide') return false;
  const row = selectDecisionRow(experiment, decisions);
  return row?.srm?.status === 'not_applicable' || row?.status === 'delivery_only';
}

/* ── Typed gateSummary evidence (AC-803) ─────────────────────────────────── */

/** FE mirror of the compact feed row simulate.ts persists (AC-805). */
export interface ProposalFeedRow {
  family: string;
  targetEntity: string;
  targetId: string;
  direction: string;
  claim: string;
  actionable: boolean;
  blockedReason?: string;
  nextEvidence?: string;
  /** Iteration D (AC-1104) — lineage refs persisted at replay finalize; absent
   * on pre-D runs (parser tolerates, card falls back to ids). */
  targetName?: string;
  analysisId?: string;
  insightIds?: string[];
  audienceId?: string;
  /** D3 §9.1 — estimator magnitude (within-audience share + interval); absent pre-D3. */
  estimate?: number;
  posteriorInterval?: [number, number];
}

/** FE mirror of the compact review block `simulate review --register-decision` persists (U7 AC-1008). */
export interface ReviewSummaryBlock {
  counts: { accepted?: number; rejected?: number; deferred?: number; undecided?: number };
  /** proposalId = `${family}:${targetId}` — joins feed rows to decisions. */
  byId: Array<{ proposalId: string; decision: string; applyMode?: string }>;
  patchHash?: string;
  unresolvedRisks?: number;
  reviewedAt?: string;
}

export interface GateSummaryEvidence {
  kind: 'evidence';
  resolver?: { answers?: number; lineageCoverage?: number; classCounts?: Record<string, number> };
  estimator?: { rows?: number; blocked?: number; gatesVersion?: string };
  proposals?: { rows?: number; actionable?: number; blocked?: number; compilerVersion?: string; top: ProposalFeedRow[] };
  calibration?: { top1Accuracy?: number; brier?: number; ece?: number };
  /** Absent on pre-U7 runs — the card renders the feed exactly as before. */
  review?: ReviewSummaryBlock;
}

export type ParsedGateSummary =
  | { kind: 'none' }
  /** Legacy failure shapes {error} / {failureReason} → one readable line. */
  | { kind: 'failure'; message: string }
  /** Pre-U5 shapes (e.g. runbook {pids, purchases, assess}) → flat key/value lines. */
  | { kind: 'legacy'; entries: Array<{ key: string; value: string }> }
  | GateSummaryEvidence;

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
const asNumber = (value: unknown): number | undefined => (typeof value === 'number' && Number.isFinite(value) ? value : undefined);
const asString = (value: unknown): string | undefined => (typeof value === 'string' && value.trim() ? value : undefined);
/** 2-tuple guard for posterior intervals — anything else degrades to absent. */
const parseInterval = (value: unknown): [number, number] | undefined =>
  Array.isArray(value) && value.length === 2 && value.every((n) => typeof n === 'number' && Number.isFinite(n))
    ? [value[0], value[1]]
    : undefined;

function parseReviewBlock(value: unknown): ReviewSummaryBlock | undefined {
  const review = asRecord(value);
  if (!review) return undefined;
  const counts = asRecord(review.counts) ?? {};
  const byId = Array.isArray(review.byId)
    ? review.byId.flatMap((entry) => {
        const row = asRecord(entry);
        const proposalId = row ? asString(row.proposalId) : undefined;
        const decision = row ? asString(row.decision) : undefined;
        if (!proposalId || !decision) return [];
        return [{ proposalId, decision, ...(asString(row!.applyMode) ? { applyMode: asString(row!.applyMode) } : {}) }];
      })
    : [];
  return {
    counts: {
      accepted: asNumber(counts.accepted),
      rejected: asNumber(counts.rejected),
      deferred: asNumber(counts.deferred),
      undecided: asNumber(counts.undecided),
    },
    byId,
    patchHash: asString(review.patchHash),
    unresolvedRisks: asNumber(review.unresolvedRisks),
    reviewedAt: asString(review.reviewedAt),
  };
}

function parseFeedRows(value: unknown): ProposalFeedRow[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    const row = asRecord(entry);
    if (!row) return [];
    const family = asString(row.family);
    const targetId = asString(row.targetId);
    if (!family || !targetId) return [];
    const insightIds = Array.isArray(row.insightIds)
      ? row.insightIds.filter((id): id is string => typeof id === 'string' && !!id.trim()).slice(0, 5)
      : [];
    return [{
      family,
      targetEntity: asString(row.targetEntity) ?? 'unknown',
      targetId,
      direction: asString(row.direction) ?? 'inconclusive',
      claim: asString(row.claim) ?? 'diagnostic',
      actionable: row.actionable === true,
      ...(asString(row.blockedReason) ? { blockedReason: asString(row.blockedReason) } : {}),
      ...(asString(row.nextEvidence) ? { nextEvidence: asString(row.nextEvidence) } : {}),
      ...(asString(row.targetName) ? { targetName: asString(row.targetName) } : {}),
      ...(asString(row.analysisId) ? { analysisId: asString(row.analysisId) } : {}),
      ...(insightIds.length ? { insightIds } : {}),
      ...(asString(row.audienceId) ? { audienceId: asString(row.audienceId) } : {}),
      ...(asNumber(row.estimate) !== undefined ? { estimate: asNumber(row.estimate) } : {}),
      ...(parseInterval(row.posteriorInterval) ? { posteriorInterval: parseInterval(row.posteriorInterval) } : {}),
    }];
  });
}

/** Tolerant parser — malformed/legacy shapes degrade, never throw (AC-803). */
export function parseGateSummary(gateSummary: Record<string, unknown> | undefined | null): ParsedGateSummary {
  if (!gateSummary || Object.keys(gateSummary).length === 0) return { kind: 'none' };

  const failure = asString(gateSummary.error) ?? asString(gateSummary.failureReason);
  if (failure) return { kind: 'failure', message: failure };

  const resolver = asRecord(gateSummary.resolver);
  const estimator = asRecord(gateSummary.estimator);
  const proposals = asRecord(gateSummary.proposals);
  const calibration = asRecord(gateSummary.calibration);

  if (resolver || estimator || proposals) {
    const parsed: GateSummaryEvidence = { kind: 'evidence' };
    if (resolver) {
      parsed.resolver = {
        answers: asNumber(resolver.answers),
        lineageCoverage: asNumber(resolver.lineageCoverage),
        classCounts: asRecord(resolver.classCounts) as Record<string, number> | undefined,
      };
    }
    if (estimator) {
      parsed.estimator = {
        rows: asNumber(estimator.rows),
        blocked: asNumber(estimator.blocked),
        gatesVersion: asString(estimator.gatesVersion),
      };
    }
    if (proposals) {
      parsed.proposals = {
        rows: asNumber(proposals.rows),
        actionable: asNumber(proposals.actionable),
        blocked: asNumber(proposals.blocked),
        compilerVersion: asString(proposals.compilerVersion),
        top: parseFeedRows(proposals.top),
      };
    }
    if (calibration) {
      parsed.calibration = {
        top1Accuracy: asNumber(calibration.top1Accuracy),
        brier: asNumber(calibration.brier),
        ece: asNumber(calibration.ece),
      };
    }
    const review = parseReviewBlock(gateSummary.review);
    if (review) parsed.review = review;
    return parsed;
  }

  const entries = Object.entries(gateSummary)
    .filter(([, value]) => ['string', 'number', 'boolean'].includes(typeof value))
    .map(([key, value]) => ({ key, value: String(value) }));
  return entries.length
    ? { kind: 'legacy', entries }
    : { kind: 'legacy', entries: [{ key: 'gateSummary', value: 'present (unrecognized shape)' }] };
}

/* ── Derived surface status (AC-802) ─────────────────────────────────────── */

export function deriveSurfaceStatus(
  experiment: ExperimentDocument,
  decisionRow: FunnelABTestDecisionRow | null,
): ExperimentSurfaceStatus {
  // Blocked wins over everything: an untrustworthy result must not read as a
  // decision opportunity (same trust-first rule as the Reports tables).
  if (decisionRow) {
    const verdict = resolveDecisionVerdict(decisionRow);
    if (verdict.deliveryOnly) return 'delivery_only';
    if (decisionRow.status === 'invalid' || verdict.sampleRatioMismatch || verdict.allocationMetadataUnknown || verdict.guardrailBreached) {
      return 'blocked_by_data_quality';
    }
  }
  if (experiment.type === 'emulation') {
    if (experiment.status === 'blocked' || experiment.status === 'error') return 'blocked_by_data_quality';
    const parsed = parseGateSummary(experiment.gateSummary);
    if (parsed.kind === 'failure') return 'blocked_by_data_quality';
    if (experiment.decision || experiment.state === 'decided') return 'decided';
    // A completed run has its evidence + proposals on the card — the operator
    // can decide (accept/reject proposals or iterate the substrate).
    if (experiment.status === 'completed') return 'ready_to_decide';
    return 'collecting';
  }

  if (experiment.decision || experiment.state === 'decided') return 'decided';
  if (decisionRow) {
    if (decisionRow.status === 'winner_declared') return 'decided';
    if (decisionRow.status === 'ready_to_choose') return 'ready_to_decide';
    return 'collecting';
  }
  // No analytics evidence — fall back to the registry lifecycle (the scan
  // service flips running→ready_to_decide from the same decision report).
  if (experiment.state === 'ready_to_decide') return 'ready_to_decide';
  return 'collecting';
}

/* ── Trust line (AC-806) — same severity semantics as Reports ────────────── */

export interface ExperimentTrustAssessment {
  severity: 'ok' | 'warning' | 'critical';
  /** What the operator can/cannot trust, in plain language. */
  text: string;
  /** What to do to make the data trustworthy (omitted when severity is ok). */
  fix?: string;
}

export function deriveTrustAssessment(
  experiment: ExperimentDocument,
  decisionRow: FunnelABTestDecisionRow | null,
): ExperimentTrustAssessment {
  if (experiment.type === 'emulation') {
    if (experiment.status === 'error') {
      return {
        severity: 'critical',
        text: 'The run failed — its evidence is incomplete.',
        fix: 'Check the debug bundle and re-run the evidence pipeline.',
      };
    }
    const parsed = parseGateSummary(experiment.gateSummary);
    if (parsed.kind === 'failure') {
      return {
        severity: 'critical',
        text: `Run finalized with a failure: ${parsed.message}`,
        fix: 'Check the debug bundle and re-run the evidence pipeline.',
      };
    }
    if (parsed.kind === 'evidence' && parsed.proposals) {
      const actionable = parsed.proposals.actionable ?? 0;
      const blocked = parsed.proposals.blocked ?? 0;
      if (actionable === 0 && blocked > 0) {
        return {
          severity: 'warning',
          text: `All ${blocked} proposals are blocked by evidence gates — estimates are not strong enough to act on yet.`,
          fix: 'Collect the evidence named on each blocked proposal, then re-run the replay.',
        };
      }
      return {
        severity: 'ok',
        text: `Evidence gates passed — ${actionable} actionable proposal${actionable === 1 ? '' : 's'} from calibrated estimates.`,
      };
    }
    if (experiment.status === 'completed') {
      return { severity: 'ok', text: 'Run completed; see the run verdicts below.' };
    }
    return { severity: 'ok', text: 'Evidence collection in progress — no verdicts yet.' };
  }

  if (decisionRow) {
    const verdict = resolveDecisionVerdict(decisionRow);
    if (verdict.deliveryOnly) {
      return {
        severity: 'ok',
        text: 'Single-variant delivery is active — ordinary analytics continue, but this epoch is not an experiment.',
      };
    }
    if (verdict.sampleRatioMismatch) {
      return {
        severity: 'critical',
        text: 'Traffic split does not match the configured weights (sample ratio mismatch) — results cannot be trusted.',
        fix: 'Check variant routing and traffic weights before reading any metric.',
      };
    }
    if (verdict.allocationMetadataUnknown) {
      return {
        severity: 'critical',
        text: decisionRow.srm?.reasonCode === 'mixed_allocation_epoch'
          ? 'Multiple allocation epochs are mixed in this window — no winner decision is allowed.'
          : 'Expected allocation metadata is unavailable — no winner decision is allowed.',
        fix: 'Choose one immutable allocation epoch or restore its allocation snapshot.',
      };
    }
    if (decisionRow.status === 'invalid') {
      return {
        severity: 'critical',
        text: 'The experiment setup is invalid — results cannot be used for a decision.',
        fix: 'Review the decision scorecard configuration before continuing.',
      };
    }
    if (verdict.guardrailBreached) {
      return {
        severity: 'critical',
        text: 'A guardrail metric regressed — do not ship on the primary metric alone.',
        fix: 'Investigate the regressed guardrail in the decision scorecard.',
      };
    }
    if (decisionRow.dataQualityFlags.length) {
      return {
        severity: 'warning',
        text: `Data quality flags on this test: ${decisionRow.dataQualityFlags.join(', ')}.`,
        fix: 'Review tracking health in Reports before making the call.',
      };
    }
    return { severity: 'ok', text: 'Data checks passed — traffic split and guardrails are clean.' };
  }

  if (experiment.abTestRef?.abTestId && experiment.state !== 'draft') {
    return {
      severity: 'warning',
      text: 'No analytics evidence for the linked A/B test in the last 30 days.',
      fix: 'Confirm the test is running and receiving traffic.',
    };
  }
  return { severity: 'ok', text: 'No evidence yet — the experiment has not produced data.' };
}

/* ── Report-scope link (AC-804) ──────────────────────────────────────────── */

/**
 * Deep-link into Reports with the experiment's scope prefilled. Uses the QW1
 * identity when the facade resolved one; falls back to the raw substrate refs.
 * Returns the bare reports route when no scope is known (never null for a
 * scoped experiment type).
 */
export function buildReportScopeLink(projectId: string, experiment: ExperimentDocument): string | null {
  if (!experiment.abTestRef && !experiment.substrate) return null;
  const base = `/project/${projectId}/reports`;
  const alias = experiment.identity?.alias ?? experiment.substrate?.alias;
  if (alias) return `${base}?alias=${encodeURIComponent(alias)}`;
  const onboardingId = experiment.identity?.onboardingId ?? experiment.substrate?.funnelId;
  const versionId = experiment.identity?.versionId ?? experiment.substrate?.versionId;
  if (onboardingId) {
    const params = new URLSearchParams({ onboardingId });
    if (versionId) params.set('versionId', versionId);
    return `${base}?${params.toString()}`;
  }
  return base;
}

/** Re-exported so card code can render verdict data without a second resolve. */
export type { DecisionVerdictResult };
